import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { FormattedMessage } from 'react-intl';

import LandingSection from '../../../MyComponents/LandingSections/LandingSection';
import SectionEyebrow from '../../../MyComponents/LandingSections/SectionEyebrow';
import { REDESIGN_COLORS, REDESIGN_FONTS } from '../../../constants';

const useStyles = makeStyles(() => ({
    layout: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))',
        gap: '56px',
    },
    intro: {
        // The design constrains the intro copy well short of its column.
        '& > h2, & > p': {
            maxWidth: '390px',
        },
    },
    eyebrow: {
        marginBottom: '18px',
    },
    title: {
        margin: '0 0 16px',
        fontSize: 'clamp(2rem, 4vw, 3.2rem)',
        lineHeight: 1.08,
        letterSpacing: '-0.035em',
        fontWeight: REDESIGN_FONTS.weightHeading,
        color: REDESIGN_COLORS.dark,
    },
    lead: {
        margin: 0,
        color: REDESIGN_COLORS.body,
    },
    list: {
        display: 'grid',
        gap: '9px',
        alignContent: 'start',
    },
    item: {
        margin: 0,
        border: `1px solid ${REDESIGN_COLORS.sand}`,
        borderRadius: '14px',
        background: REDESIGN_COLORS.white,
        '&[open] $chevron': {
            transform: 'rotate(180deg)',
        },
    },
    summary: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '15px 20px',
        color: REDESIGN_COLORS.questionText,
        fontSize: '0.98rem',
        fontWeight: REDESIGN_FONTS.weightStrong,
        cursor: 'pointer',
        // Suppress the native disclosure triangle in both engine families.
        listStyle: 'none',
        '&::-webkit-details-marker': {
            display: 'none',
        },
        '&:hover': {
            color: REDESIGN_COLORS.link,
        },
    },
    summaryText: {
        flex: 1,
    },
    // The design gives these rows no open/closed affordance at all, which makes
    // them hard to discover as controls; this is a small chevron that turns.
    chevron: {
        flexShrink: 0,
        transition: 'transform 150ms ease',
    },
    answer: {
        padding: '0 20px 16px',
        color: REDESIGN_COLORS.body,
        fontSize: '0.9rem',
    },
}));

/**
 * The FAQ band between pricing and the closing CTA. Native <details> rather than
 * the app's CustomizedExpansionPanels: that one is styled for the whiteboard
 * drawer and is in use there, and native disclosure keeps the answers in the
 * markup without any JavaScript.
 */
const HomeFaq = ({ keyPrefix = 'redesign.home.faq', count = 6 }) => {
    const classes = useStyles();
    const questions = Array.from({ length: count }, (_, i) => i + 1);

    return (
        <LandingSection paddingTop={56} paddingBottom={64}>
            <div className={classes.layout}>
                <div className={classes.intro}>
                    <SectionEyebrow className={classes.eyebrow}>
                        <FormattedMessage id={`${keyPrefix}.eyebrow`} />
                    </SectionEyebrow>
                    <h2 className={classes.title}>
                        <FormattedMessage id={`${keyPrefix}.title`} />
                    </h2>
                    <p className={classes.lead}>
                        <FormattedMessage id={`${keyPrefix}.lead`} />
                    </p>
                </div>

                <div className={classes.list}>
                    {questions.map((n) => (
                        // The design leaves the first question open.
                        <details key={n} className={classes.item} open={n === 1}>
                            <summary className={classes.summary}>
                                <span className={classes.summaryText}>
                                    <FormattedMessage id={`${keyPrefix}.q${n}`} />
                                </span>
                                <svg
                                    className={classes.chevron}
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2.4"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    aria-hidden="true"
                                >
                                    <polyline points="6 9 12 15 18 9" />
                                </svg>
                            </summary>
                            <div className={classes.answer}>
                                <FormattedMessage id={`${keyPrefix}.a${n}`} />
                            </div>
                        </details>
                    ))}
                </div>
            </div>
        </LandingSection>
    );
};

export default HomeFaq;
